import toast from 'react-hot-toast'
import { openNotRegisteredPopup } from '../store/slices/ui.slice' // ✅ מייבא רק את ה-ACTION
import { addItem } from '../store/slices/watchlist.slice'
import { fetchAnimesByIds } from '../store/slices/animeDB.slice'

export const utilService = {
  saveToLocalStorage,
  getFromLocalStorage,
  removeFromLocalStorage,
  makeId,
  createTimestamp,
  timeAgo,
  formatDateToHebrew,
  isSameDay,
  dateDifferenceInHebrew,
  delay,
  truncateText,
  slugify,
  isValidEmail,
  debounce,
  handleAddItem,
  isValidUsername,
  clearStorage,
}

// -------------------- LocalStorage Management --------------------
// שמירת נתונים ל-LocalStorage
function saveToLocalStorage(key, value) {
  localStorage.setItem(
    key,
    typeof value === 'string' ? value : JSON.stringify(value)
  )
}

export function isValidUsername(username) {
  const pattern = /^(?=(?:.*[a-z]){3,})[a-z0-9_]{3,15}$/
  return pattern.test(username)
}

// שליפת נתונים מ-LocalStorage
function getFromLocalStorage(key) {
  const value = localStorage.getItem(key)
  try {
    return JSON.parse(value) // אם זה JSON
  } catch {
    return value // אם זה טקסט רגיל
  }
}

// הסרת נתונים מ-LocalStorage
export function removeFromLocalStorage(key) {
  localStorage.removeItem(key)
}

// -------------------- ID Management --------------------
// יצירת מזהה ייחודי
export function makeId(length = 6) {
  let txt = ''
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

  for (let i = 0; i < length; i++) {
    txt += possible.charAt(Math.floor(Math.random() * possible.length))
  }

  return txt
}

// יצירת Timestamp
export function createTimestamp() {
  return Date.now()
}

// -------------------- Time Ago in Hebrew --------------------
// תרגום זמן שחלף בעברית
export function timeAgo(timestamp) {
  const now = new Date()
  const time = new Date(timestamp)
  const secondsPast = Math.floor((now - time) / 1000)

  if (secondsPast < 60) {
    return 'לפני כמה רגעים'
  }
  if (secondsPast < 3600) {
    return `לפני ${Math.floor(secondsPast / 60)} דקות`
  }
  if (secondsPast < 86400) {
    return `לפני ${Math.floor(secondsPast / 3600)} ${
      Math.floor(secondsPast / 3600) === 1 ? 'שעה' : 'שעות'
    }`
  }
  if (secondsPast < 172800) {
    return 'אתמול'
  }
  if (secondsPast < 604800) {
    return `לפני ${Math.floor(secondsPast / 86400)} ימים`
  }
  if (secondsPast < 2592000) {
    return `לפני ${Math.floor(secondsPast / 604800)} ${
      Math.floor(secondsPast / 604800) === 1 ? 'שבוע' : 'שבועות'
    }`
  }
  if (secondsPast < 31536000) {
    return `לפני ${Math.floor(secondsPast / 2592000)} ${
      Math.floor(secondsPast / 2592000) === 1 ? 'חודש' : 'חודשים'
    }`
  }
  return `לפני ${Math.floor(secondsPast / 31536000)} ${
    Math.floor(secondsPast / 31536000) === 1 ? 'שנה' : 'שנים'
  }`
}

// -------------------- Date Handling --------------------
// פורמט תאריך לעברית לפי ישראל
export function formatDateToHebrew(date) {
  const options = {
    weekday: 'long', // יום בשבוע
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Jerusalem',
  }
  return new Intl.DateTimeFormat('he-IL', options).format(new Date(date))
}

// האם שני תאריכים זהים לפי יום
export function isSameDay(date1, date2) {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  )
}

// קבלת ההפרש בין תאריכים בעברית
export function dateDifferenceInHebrew(date1, date2) {
  const diffInMilliseconds = Math.abs(new Date(date1) - new Date(date2))
  const diffInDays = Math.ceil(diffInMilliseconds / (1000 * 60 * 60 * 24))
  return diffInDays === 1 ? 'יום אחד' : `${diffInDays} ימים`
}

// -------------------- Miscellaneous --------------------
// עיכוב פעולה אסינכרונית
export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function debounce(func, wait) {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// קיצור טקסטים
export function truncateText(text, maxLength) {
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

// הפיכת טקסט ל-slug
export function slugify(text) {
  return text
    .toString() // המרה למחרוזת
    .trim() // הסרת רווחים מסביב
    .replace(/[\u0590-\u05FF]/g, (char) => char) // שמירה על אותיות בעברית
    .replace(/[\s\W-]+/g, '-') // המרת רווחים ותווים מיוחדים למקף
    .replace(/^-+|-+$/g, '') // הסרת מקפים מיותרים בתחילת וסוף המחרוזת
}

// בדיקת כתובת אימייל תקינה
export function isValidEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(String(email).toLowerCase())
}

export function getAnimeDetailsUrl(anime) {
  if (!anime || (!anime.mal_id && !anime.anizone_id)) return '#' // מחזיר '#' אם חסר ID

  const animeId = anime.mal_id || anime.anizone_id // עדיפות קודם ל-mal_id, ואז anizone_id
  const slugTitle = (
    anime.title_english ||
    anime.title ||
    anime.name ||
    'anime'
  )
    .trim()
    .replace(/\s+/g, '_') // הופך רווחים ל- "_"
    .replace(/[^\w-]/g, '') // מסיר כל תו שאינו אות, מספר או מקף

  return `/anime/${animeId}/${slugTitle}`
}


export function handleAddItem(
  statusValue,
  item,
  dispatch,
  loggedInUser,
  watchlist
) {
  if (!loggedInUser) {
    toast.error('עליך להתחבר כדי להוסיף אנימות לרשימה!')
    dispatch(openNotRegisteredPopup())
    return
  }

  const anizoneId = item.anizone_id ?? item.mal_id
  if (typeof anizoneId === 'undefined' || anizoneId === null) {
    console.error('❌ handleAddItem: Missing anizone_id or mal_id', item)
    toast.error('אירעה שגיאה בזיהוי האנימה')
    return
  }

  const exists = watchlist.some(
    (watchItem) => watchItem.anizone_id === anizoneId
  )
  if (exists) {
    toast.error('האנימה כבר קיימת ברשימת הצפייה!')
    return
  }

  try {
    const today = new Intl.DateTimeFormat('he-IL', {
      timeZone: 'Asia/Jerusalem',
    }).format(new Date())

    const parsedEpisodes = parseInt(item.episodes)
    const isCompleted = statusValue === 'Completed'
    const isValidEpisodes = !isNaN(parsedEpisodes) && parsedEpisodes > 0

    const formattedItem = {
      anizone_id: anizoneId,
      status: statusValue,
      progress: isCompleted && isValidEpisodes ? parsedEpisodes : 0,
      start_date: today,
      finish_date: isCompleted ? today : null,
    }

    // ➊ קודם שמירה
    const thunk = dispatch(
      addItem({
        userId: loggedInUser._id,
        listType: 'watchlist',
        item: formattedItem,
      })
    )

    // ➋ ואז הבאת המטא-דטה לאחר ההוספה
    thunk.unwrap().finally(() => {
      dispatch(fetchAnimesByIds([anizoneId]))
    })

    toast.success('האנימה נוספה לרשימה בהצלחה!')
  } catch (err) {
    console.error('❌ handleAddItem error:', err)
    toast.error('שגיאה בהוספת האנימה לרשימה!')
  }
}

export function formatHebrewDate(timestamp) {
  return new Date(timestamp).toLocaleDateString('he-IL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function clearStorage() {
  // ניקוי LocalStorage
  localStorage.clear()

  // ניקוי SessionStorage
  sessionStorage.clear()

  // מחיקת כל הקבצים ב-Cache API
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name))
    })
  }

  // ניקוי כל העוגיות (Cookies)
  document.cookie.split(';').forEach((cookie) => {
    document.cookie = cookie
      .replace(/^ +/, '')
      .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`)
  })

  // מחיקת כל מסדי הנתונים של IndexedDB
  if (window.indexedDB) {
    let databases = indexedDB.databases
      ? indexedDB.databases()
      : Promise.resolve([])
    databases.then((dbs) => {
      dbs.forEach((db) => {
        indexedDB.deleteDatabase(db.name)
      })
    })
  }

  // ביטול Service Workers כדי למנוע מטמון ישן
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister()
      })
    })
  }

  // ביטול כל ה-Push Notifications אם קיימים
  if ('Notification' in window && navigator.serviceWorker) {
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.pushManager) {
        registration.pushManager.getSubscription().then((subscription) => {
          if (subscription) {
            subscription.unsubscribe()
          }
        })
      }
    })
  }
}

export function getAnimeImage(animeData) {
  return (
    animeData?.images?.webp?.large_image_url ||
    animeData?.images?.jpg?.large_image_url ||
    animeData?.images?.webp?.image_url ||
    animeData?.images?.jpg?.image_url ||
    animeData?.image_url ||
    'https://placehold.co/150X180'
  )
}
